import apiClient from './apiClient';

export interface TeamMemberSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export interface TeamListItem {
  id: string;
  name: string;
  teamType: string;
  description?: string | null;
  teamLeadId?: string | null;
  maxConcurrentTasks?: number | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  members?: TeamMemberSummary[];
}

export const EXECUTION_TEAM_TYPES = ['construction', 'subcontractor'];

export const isExecutionTeam = (team: Pick<TeamListItem, 'teamType'>): boolean =>
  EXECUTION_TEAM_TYPES.includes(String(team.teamType ?? '').toLowerCase());

export interface CreateTeamPayload {
  name: string;
  teamType: string;
  description?: string;
  teamLeadId?: string;
  maxConcurrentTasks?: number;
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  const envelope = body as ApiEnvelope<T>;
  return envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
    ? (envelope.data as T)
    : (body as T);
};

export interface TeamOverviewRow {
  id: string;
  name: string;
  teamType: string;
  isActive: boolean;
  maxConcurrentTasks: number;
  teamLeadId: string | null;
  teamLeadName: string | null;
  memberCount: number;
  workload: {
    inProgress: number;
    blocked: number;
    scheduled: number;
    inspectionPending: number;
    completed: number;
    cancelled: number;
  };
  performance: {
    tasksTouched: number;
    firstPassYieldPercent: number | null;
    openDefects: number;
    closedDefects: number;
    reworkCount: number;
  };
  payment: {
    totalApproved: number;
    totalPaid: number;
    awaitingApproval: number;
    paidCount: number;
  };
}

export interface TeamBonusPointsRow {
  id: string;
  projectId: string;
  teamId: string;
  teamName: string | null;
  periodStart: string;
  periodEnd: string;
  qualityPoints: number;
  speedPoints: number;
  attendancePoints: number;
  totalPoints: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamBonusPointsSummary {
  projectId: string;
  teamId?: string;
  totals: {
    qualityPoints: number;
    speedPoints: number;
    attendancePoints: number;
    totalPoints: number;
  };
  rows: TeamBonusPointsRow[];
}

class TeamService {
  async getTeams(): Promise<TeamListItem[]> {
    const response = await apiClient.get<ApiEnvelope<TeamListItem[]>>('/teams');
    return unwrap<TeamListItem[]>(response) || [];
  }

  async getTeam(id: string): Promise<TeamListItem> {
    const response = await apiClient.get<ApiEnvelope<TeamListItem>>(`/teams/${id}`);
    return unwrap<TeamListItem>(response);
  }

  async createTeam(payload: CreateTeamPayload): Promise<TeamListItem> {
    const response = await apiClient.post<ApiEnvelope<TeamListItem>>('/teams', payload);
    return unwrap<TeamListItem>(response);
  }

  async addTeamMember(teamId: string, userId: string): Promise<any> {
    const response = await apiClient.post<ApiEnvelope<any>>('/teams/members', { teamId, userId });
    return unwrap<any>(response);
  }

  async getOverview(projectId?: string): Promise<TeamOverviewRow[]> {
    const params: Record<string, string> = {};
    if (projectId) params.projectId = projectId;
    const response = await apiClient.get<ApiEnvelope<TeamOverviewRow[]>>('/teams/overview', { params });
    return unwrap<TeamOverviewRow[]>(response) || [];
  }

  async getBonusPoints(params: { projectId: string; teamId?: string }): Promise<TeamBonusPointsSummary> {
    const response = await apiClient.get<ApiEnvelope<TeamBonusPointsSummary>>('/teams/bonus-points', { params });
    return unwrap<TeamBonusPointsSummary>(response) || {
      projectId: params.projectId,
      teamId: params.teamId,
      totals: { qualityPoints: 0, speedPoints: 0, attendancePoints: 0, totalPoints: 0 },
      rows: [],
    };
  }

  async removeTeamMember(teamId: string, userId: string): Promise<void> {
    await apiClient.delete(`/teams/${teamId}/members/${userId}`);
  }

  async deleteTeam(teamId: string): Promise<void> {
    await apiClient.delete(`/teams/${teamId}`);
  }
}

export default new TeamService();
